import { Text } from '@thanx/uikit/text'
import { Order, OrderItem, OrderModifier } from 'api/orders'
import Modal from 'components/ModalContainer'
import { buildTranslate } from 'locales'
import { OrderLineModifier } from 'models/Order'
import React, { useMemo } from 'react'
import { merchantTheme } from 'theme'
import { getDateWithTimeAndZone } from 'utilities/date'
import { currencyFormatter } from 'utilities/formatters'
import { textUtils } from 'utilities/textUtils'
import { useUserTimeZone } from 'utilities/userTimeZone'
import { SCROLLABLE_DIALOG_STYLE } from '../OrderDetailsModal'
import ReceiptTable from '../OrderReceipt/Table'
import OrderDetailsModalProviderOloSection from './OrderDetailsModalProviderOloSection'
import OrderDetailsModalProviderSection from './OrderDetailsModalProviderSection'
import OrderGiftCardTransactions from './OrderGiftCardTransactions'
import StatusBadge from './StatusBadge'

const t = buildTranslate('users.view.activity.orders.detail')
const tReceipt = buildTranslate('users.view.activity.order_details.receipt')

type PropsT = {
  show?: boolean
  order: Order
  itemsEnabled: boolean
  onHide: () => void
}

// An unset quantity arrives as 0 and must render blank, not "0".
const toQuantity = (quantity: number): string | null =>
  quantity ? String(quantity) : null

const toCents = (dollars: number): number => Math.round(dollars * 100)

// Every provider except Square already extends `price` across quantity
// (Toast/Deliverect: OrderItem::CalculateMenuPrice; Olo: totalcost). Square
// snapshots the per-unit menu price as-is (Square::Order::Submit#snapshot_items!
// in thanx-ordering), so only Square needs multiplying out here.
// An unset (proto3-default 0) quantity multiplies as 1, matching toQuantity above.
const toLineTotalCents = (item: OrderItem, provider: string): number =>
  provider === 'square'
    ? toCents(item.price * (item.quantity || 1))
    : toCents(item.price)

// parent_item_modifier_id nests a combo's own modifier (e.g. a sauce
// choice); a dangling, self, or cyclic reference falls back to top-level.
// -1: proto3's int64 default is 0, so a real id can be 0 and can't double as the sentinel.
const ROOT_PARENT_ID = -1

const toModifierTree = (modifiers: OrderModifier[]): OrderLineModifier[] => {
  const byId = new Map(
    modifiers.map(modifier => [modifier.item_modifier_id, modifier])
  )

  // A cycle in the declared parent chain would otherwise make every
  // modifier in it disappear, since none of them ever resolves to root.
  const chainCycles = (modifier: OrderModifier): boolean => {
    const seen = new Set<number>([modifier.item_modifier_id])
    let parentId = modifier.parent_item_modifier_id
    while (byId.has(parentId)) {
      if (seen.has(parentId)) return true
      seen.add(parentId)
      parentId = (byId.get(parentId) as OrderModifier).parent_item_modifier_id
    }
    return false
  }

  const childrenByParentId = new Map<number, OrderModifier[]>()
  modifiers.forEach(modifier => {
    const parentId =
      byId.has(modifier.parent_item_modifier_id) &&
      modifier.parent_item_modifier_id !== modifier.item_modifier_id &&
      !chainCycles(modifier)
        ? modifier.parent_item_modifier_id
        : ROOT_PARENT_ID
    childrenByParentId.set(parentId, [
      ...(childrenByParentId.get(parentId) ?? []),
      modifier,
    ])
  })

  const build = (parentId: number): OrderLineModifier[] =>
    (childrenByParentId.get(parentId) ?? []).map(modifier => ({
      name: modifier.name,
      quantity: toQuantity(modifier.quantity),
      modifiers: build(modifier.item_modifier_id),
    }))

  return build(ROOT_PARENT_ID)
}

const OrderDetailsModal = ({
  show = true,
  order,
  itemsEnabled,
  onHide,
}: PropsT) => {
  const { colors } = merchantTheme
  const timeZone = useUserTimeZone()

  const receiptItems = useMemo(
    () =>
      (order.items ?? []).map(item => ({
        name: item.name,
        quantity: toQuantity(item.quantity),
        price_cents: toLineTotalCents(item, order.ordering_provider),
        notes: item.notes || undefined,
        modifiers: toModifierTree(item.modifiers ?? []),
      })),
    [order.items, order.ordering_provider]
  )
  const showReceipt = itemsEnabled && receiptItems.length > 0

  return (
    <Modal
      isOpen={show}
      title={t('order')}
      proceedText={t('okay')}
      className="fs-unmask"
      hideCancel
      onClose={onHide}
      onProceed={onHide}
      dialogStyle={showReceipt ? SCROLLABLE_DIALOG_STYLE : undefined}
      zIndex={3000}
    >
      <div>
        <div className="mt-m">
          <Text.Header4 className="mb-m" tag="div" bold>
            {t('title')}
          </Text.Header4>

          {order.ordering_provider !== 'olo' ? (
            <OrderDetailsModalProviderSection order={order} />
          ) : (
            <OrderDetailsModalProviderOloSection order={order} />
          )}

          {!!order.location && (
            <>
              <Text.BodyText4 className="mb-xs mt-s" tag="div" bold>
                {t('location')}
              </Text.BodyText4>

              {[
                order.location.name,
                order.location.street,
                `${order.location.city}, ${order.location.state} ${order.location.zip}`,
              ].map(locationItem => (
                <Text.BodyText4
                  key={locationItem}
                  tag="div"
                  color={colors.grey70}
                >
                  {locationItem}
                </Text.BodyText4>
              ))}
            </>
          )}

          <Text.BodyText4 className="mb-xs mt-s" tag="div" bold>
            {t('status')}
          </Text.BodyText4>

          <Text.BodyText4 tag="div">
            {<StatusBadge status={order.status} />}
          </Text.BodyText4>

          <Text.BodyText4 className="mb-xs mt-s" tag="div" bold>
            {t('handoff')}
          </Text.BodyText4>

          <Text.BodyText4 tag="div">
            {textUtils.titleize(order.handoff)}
          </Text.BodyText4>

          <Text.BodyText4 className="mb-xs mt-s" tag="div" bold>
            {t('placed_at')}
          </Text.BodyText4>

          <Text.BodyText4 tag="div">
            {getDateWithTimeAndZone(order.placed_at, timeZone)}
          </Text.BodyText4>

          {order.gift_card_transactions && (
            <OrderGiftCardTransactions
              transactions={order.gift_card_transactions}
            />
          )}

          <Text.BodyText4 className="mb-xs mt-s" tag="div" bold>
            {t('amounts')}
          </Text.BodyText4>

          <Text.BodyText4 tag="div">
            {t('subtotal')}: {currencyFormatter(order.price_breakdown.subtotal)}
          </Text.BodyText4>

          {/* Item line totals are gross; subtotal is net of discount. */}
          {order.price_breakdown.total_discount !== 0 && (
            <Text.BodyText4 tag="div">
              {t('discount')}: -
              {currencyFormatter(order.price_breakdown.total_discount)}
            </Text.BodyText4>
          )}

          <Text.BodyText4 tag="div">
            {t('tax')}: {currencyFormatter(order.price_breakdown.total_tax)}
          </Text.BodyText4>

          <Text.BodyText4 tag="div">
            {t('tip')}: {currencyFormatter(order.price_breakdown.tip)}
          </Text.BodyText4>

          <Text.BodyText4 tag="div">
            {t('total')}: {currencyFormatter(order.price_breakdown.total)}
          </Text.BodyText4>

          {showReceipt && (
            <>
              <Text.BodyText4 className="mb-xs mt-s" tag="div" bold>
                {tReceipt('heading')}
              </Text.BodyText4>

              <ReceiptTable items={receiptItems} />
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default OrderDetailsModal
